import { EmailTemplateMetadata } from '../hooks/useComms';

/** KINDLY-1150 Communications IA labels (routes stay under /templates). */
export const COMMS_IA = {
  nav: 'Communications',
  pageHeading: 'Communications',
  transactionalMessages: 'Transactional messages',
} as const;

/** Message.audience values (KINDLY-1149). Distinct from Mandrill template labels. */
export const SYSTEM_MESSAGE_AUDIENCES = ['member', 'peer', 'admin', 'shared'] as const;
export type SystemMessageAudience = (typeof SYSTEM_MESSAGE_AUDIENCES)[number];

export const AUDIENCE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'member', label: 'Member' },
  { key: 'peer', label: 'Peer' },
  { key: 'admin', label: 'Admin' },
  { key: 'shared', label: 'Shared' },
] as const;

export type AudienceFilterKey = (typeof AUDIENCE_FILTERS)[number]['key'];

/** Form/editor options — excludes the list "All" sentinel. */
export const AUDIENCE_OPTIONS = AUDIENCE_FILTERS.filter((item) => item.key !== 'all');

export const CHANNEL_TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
] as const;

export type ChannelTypeFilterKey = (typeof CHANNEL_TYPE_FILTERS)[number]['key'];

/** Group list filter: all groups, ungrouped, or a specific group id string. */
export type GroupFilterKey = 'all' | 'null' | string;

export function buildCommsListParams(input: {
  groupFilter: GroupFilterKey;
  audienceFilter: AudienceFilterKey;
  searchText: string;
}): Record<string, unknown> {
  const params: Record<string, unknown> = { limit: 1000 };

  if (input.groupFilter === 'null') {
    params.group_id = 'null';
  } else if (input.groupFilter !== 'all') {
    params.group_id = input.groupFilter;
  }

  if (input.audienceFilter !== 'all') {
    params.audience = input.audienceFilter;
  }
  if (input.searchText.length > 0) {
    params.search_term = input.searchText;
  }
  return params;
}

/** Client-side channel filter — API does not expose send_email/send_sms query params. */
export function applyChannelTypeFilter<T extends { send_email?: boolean; send_sms?: boolean }>(
  messages: T[] | null | undefined,
  typeFilter: ChannelTypeFilterKey,
): T[] {
  const list = messages || [];
  if (typeFilter === 'email') return list.filter((item) => !!item.send_email);
  if (typeFilter === 'sms') return list.filter((item) => !!item.send_sms);
  return list;
}

/**
 * Post KINDLY-1146 twin collapse, Switchboard keeps one active row per key on the
 * kindlyhuman domain. Creates (no existing domain_id) default to that canonical id.
 */
export const CANONICAL_COMMS_DOMAIN_KEY = 'kindlyhuman';

export function resolveCommsSaveDomainId(input: {
  existingDomainId?: number | null;
  domains?: Array<{ id: number; key: string }> | null;
  canonicalKey?: string;
}): number | undefined {
  if (input.existingDomainId != null) {
    return input.existingDomainId;
  }
  const key = input.canonicalKey || CANONICAL_COMMS_DOMAIN_KEY;
  return (input.domains || []).find((domain) => domain.key === key)?.id;
}

/**
 * Mandrill template label audience (shell taxonomy), not system_messages.audience.
 * Used only for picker option text.
 */
export function getAudienceFromLabels(labels: string[] | undefined | null): SystemMessageAudience | null {
  const labs = labels ?? [];
  for (const audience of SYSTEM_MESSAGE_AUDIENCES) {
    if (labs.includes(audience)) return audience;
  }
  return null;
}

export function isRetiredTemplate(labels: string[] | undefined | null): boolean {
  return (labels ?? []).includes('retired');
}

export function isInUseTemplate(labels: string[] | undefined | null): boolean {
  return (labels ?? []).includes('in-use');
}

/** Human-readable Mandrill option text; value remains the slug (`id`). */
export function formatEmailTemplateOptionLabel(template: EmailTemplateMetadata): string {
  const labels = template.labels ?? [];
  const meta: string[] = [];
  const audience = getAudienceFromLabels(labels);
  if (audience) meta.push(audience);
  if (labels.includes('kc')) meta.push('kc');
  if ((template.id || '').includes('-global')) meta.push('global');
  if (isInUseTemplate(labels)) meta.push('in-use');
  if (isRetiredTemplate(labels)) meta.push('retired');

  const title = template.name || template.id;
  const slugSuffix = template.name && template.name !== template.id ? ` (${template.id})` : '';
  const metaSuffix = meta.length ? ` · ${meta.join(' · ')}` : '';
  return `${title}${slugSuffix}${metaSuffix}`;
}

/** Prefer in-use templates; hide retired unless currently selected. */
export function templatesForPicker(
  templates: EmailTemplateMetadata[] | undefined,
  selectedId?: string | null,
): EmailTemplateMetadata[] {
  if (!templates?.length) return [];
  const selected = selectedId && selectedId !== 'No Template' ? selectedId : null;
  const filtered = templates.filter((t) => !isRetiredTemplate(t.labels) || t.id === selected);
  return [...filtered].sort((a, b) => {
    const aInUse = isInUseTemplate(a.labels) ? 0 : 1;
    const bInUse = isInUseTemplate(b.labels) ? 0 : 1;
    if (aInUse !== bInUse) return aInUse - bInUse;
    return (a.name || a.id).localeCompare(b.name || b.id, undefined, { sensitivity: 'base' });
  });
}
